/**
 * What a signed-out visitor sees instead of an app that is no use to them.
 *
 * Signed out, LunaPin's chrome is entirely useless: a tab strip holding one empty tab,
 * a sidebar listing it, a "+" that creates windows nobody can keep. **Cover it.** That
 * is not decoration — it is the difference between a product that has nothing for you
 * yet and one that looks broken.
 *
 * The pattern is the sibling Lunicle's `EmptyTabSurface`, and three of its details are
 * copied deliberately because each is a failure somebody has already had:
 *
 *  - **Driven from `main.kt`, not from the toolkit.** This has to *outrank* the chrome,
 *    so it cannot be mounted inside it. `AppShellSpec.emptyTabContent` is the wrong
 *    seam and should not be stretched into being the right one: it paints inside the
 *    main area, under the tab strip.
 *  - **The shell keeps running underneath.** Hiding or unmounting it is the tempting
 *    move and is a trap: the toolkit re-measures pane geometry against the main slot's
 *    box, so a shell that was hidden while a session arrived lays its first windows out
 *    against a zero-sized rectangle and they come back wrong. So it is covered and
 *    marked inert instead — see [coverShell], which also explains why `inert` alone is
 *    not the whole of it.
 *  - **On `<body>`, at a z-index above the top bar.** The top bar is its own stacking
 *    context, so anything that must sit above this surface has to be on `<body>` too.
 *    The toolkit's hover menus already are.
 *
 * ── One card, at most one button ─────────────────────────────────────────────
 *
 * A mark, a headline, one line of explanation, and either one button or none. Exactly
 * one action, because the reader has arrived somewhere with nothing on it and the
 * useful answer is the single next thing to do rather than a menu. And **no button at
 * all** where there is nothing to offer, never a disabled one: a control that cannot
 * work reads as the app being broken, which is the impression this surface exists to
 * prevent.
 *
 * The button opens the *same* dialog the toolbar corner opens, by calling the same
 * function. The dialog is where the form lives; the card is where the invitation lives.
 *
 * Every word on it comes from [SessionBackingViewModel]; none is written here. Three
 * situations get three sets of words, and the two that are easiest to leave unworded — an
 * instance with no sign-in configured at all, and a server that did not answer — are the
 * ones a fresh clone and a stopped server land on. A view that wrote its own copy would be
 * the second place they lived.
 *
 * ── What this card does *not* say ────────────────────────────────────────────
 *
 * A failed sign-in attempt. `SignedOut` carries the message, and this card deliberately
 * ignores it: at the moment it is set the dialog is up, directly over this card, with the
 * banner in it — so painting the sentence here as well would be one failure reported twice
 * on one screen, which reads as two problems. And by the time the dialog is gone the
 * message has been cleared, because dismissing it tells the model so.
 *
 * @see SessionBackingViewModel.State.SignedOut
 */
package se.soderbjorn.lunapin.web

import kotlinx.browser.document
import org.w3c.dom.HTMLElement
import se.soderbjorn.lunapin.client.viewmodel.SessionBackingViewModel

/**
 * Raises and dismisses the landing surface to match the state.
 *
 * @param shell the element the app shell is mounted into — covered and made inert
 *   while the surface is up, never hidden.
 * @param onSignIn what the card's one button does. `main.kt` passes
 *   [SignInView.startSignIn], which is the point: one door, two ways to knock on it.
 * @param host where the surface is appended. `<body>` in production, and it has to be
 *   for the stacking reason in the file header; a parameter only so a test can watch a
 *   container of its own instead of the whole document.
 */
class LandingSurface(
    private val shell: HTMLElement,
    private val onSignIn: () -> Unit,
    private val host: HTMLElement? = document.body,
) {
    private var surface: HTMLElement? = null
    private var card: HTMLElement? = null

    /**
     * The words the card is currently showing.
     *
     * The flow behind [render] emits more than once per visible change, and repainting an
     * identical card is a flash for no reason. Its own three fields rather than the whole
     * [SessionBackingViewModel.State.SignedOut], so that the one field this card does not
     * render — a failed attempt's message, which belongs to the dialog — cannot repaint it.
     * Typing a wrong password would otherwise flash the card behind the dialog twice: once
     * when the failure arrives and once when the next keystroke clears it.
     */
    private var shown: Shown? = null

    /** Exactly what the card paints. See [shown]. */
    private data class Shown(
        val headline: String,
        val detail: String,
        val canSignIn: Boolean,
    )

    /**
     * Raise, refill or dismiss to match [state].
     *
     * Only [SessionBackingViewModel.State.signedIn] takes the surface down, and only
     * [SessionBackingViewModel.State.signedOut] fills it with words. The two states in
     * between are deliberately handled by doing nothing:
     *
     *  - **Loading** raises a bare, wordless surface. It is the first state of every
     *    load, and covering the shell during it is what stops a signed-out visitor
     *    seeing a flash of unusable chrome before the card lands. Wordless because
     *    nobody has been told anything yet, which is the same reason the account corner
     *    renders nothing in this state.
     *  - **Signing in** keeps whatever card is already up. Rebuilding it would mean a
     *    flash, and taking it down would show the shell for the length of a round trip
     *    and then hide it again.
     */
    fun render(state: SessionBackingViewModel.State) {
        if (state.signedIn != null) {
            dismiss()
            return
        }
        raise()
        state.signedOut?.let { fill(it) }
    }

    private fun raise() {
        if (surface != null) return
        val el = element("div", "lp-landing")
        val newCard = element("div", "lp-landing-card")
        el.appendChild(newCard)
        host?.appendChild(el)
        surface = el
        card = newCard
        coverShell(true)
    }

    private fun dismiss() {
        surface?.let { it.parentNode?.removeChild(it) }
        surface = null
        card = null
        shown = null
        coverShell(false)
    }

    /**
     * Rebuild the card's contents.
     *
     * Wholesale, when the words change. There is no caret and no scroll position on it to
     * lose, so a rebuild is cheaper to reason about than a diff — and it is the branch that
     * cannot leave a stale button behind when a configured instance answers that it is not
     * one.
     */
    private fun fill(signedOut: SessionBackingViewModel.State.SignedOut) {
        val next = Shown(
            headline = signedOut.headline,
            detail = signedOut.detail,
            canSignIn = signedOut.canSignIn,
        )
        if (next == shown) return
        shown = next
        val target = card ?: return
        target.innerHTML = ""
        // The product's name, always, and small. One of the two headlines is about a
        // situation rather than about LunaPin ("Sign-in is not configured"), and a page
        // that never says what software it is leaves a reader who arrived by a link with
        // no idea where they are.
        target.appendChild(element("div", "lp-landing-mark", "LunaPin"))
        target.appendChild(element("h1", "lp-landing-headline", next.headline))
        target.appendChild(element("p", "lp-landing-detail", next.detail))
        // No button where there is nothing to offer. See the file header.
        if (next.canSignIn) {
            target.appendChild(button("Sign in…", "lp-landing-action") { onSignIn() })
        }
    }

    /**
     * Cover the shell without hiding it.
     *
     * Three things, and they overlap on purpose. `inert` is the modern answer and does
     * all of it — no pointer events, no tab stops, hidden from assistive technology —
     * but it is a property rather than a style, so nothing in the stylesheet documents
     * it and a browser that does not have it fails silently and completely. The class
     * carries `pointer-events: none` where the stylesheet can be read, and `aria-hidden`
     * carries the part that matters most if either of the other two is missing: a reader
     * who cannot see the cover must not be walked through a tab strip that is behind a
     * page.
     *
     * What is deliberately absent is `display: none`. See the file header.
     */
    private fun coverShell(covered: Boolean) {
        shell.classList.toggle("lp-shell-covered", covered)
        if (covered) {
            shell.setAttribute("aria-hidden", "true")
        } else {
            shell.removeAttribute("aria-hidden")
        }
        shell.asDynamic().inert = covered
    }
}
